import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Shield, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOnboardingStore } from '@/hooks/use-onboarding-store';
import { initiatePayment, generateReference } from '@/services/paystackService';
import { useStripe } from '@/hooks/use-stripe';
import { toast } from 'sonner';

interface Step10PaymentProps {
  onNext: () => void;
}

const Step10Payment: React.FC<Step10PaymentProps> = ({ onNext }) => {
  const { selectedPlan, email, phone } = useOnboardingStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('nigeria');
  const [customerData, setCustomerData] = useState({
    name: ''
  });
  const { createPayment } = useStripe();

  const plans = [
    {
      id: '1-week',
      title: '1-Week No-Code Sprint',
      amount: 1330000, // in kobo
      total: '₦13,300',
      usdTotal: '$10.50',
      usdPrice: 10.50
    },
    {
      id: '2-week',
      title: '2-Week App Builder Accelerator',
      amount: 2100000, // in kobo
      total: '₦21,000',
      usdTotal: '$18.20',
      usdPrice: 18.20
    },
    {
      id: '4-week',
      title: '4-Week Full Stack No-Code Mastery',
      amount: 3700000, // in kobo
      total: '₦37,000',
      usdTotal: '$37.20',
      usdPrice: 37.20
    }
  ];

  const currentPlan = plans.find(p => p.id === selectedPlan) || plans[1];

  const handleInputChange = (field: string, value: string) => {
    setCustomerData(prev => ({ ...prev, [field]: value }));
  };

  const handlePayment = async () => {
    if (!customerData.name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!phone) {
      toast.error('Phone number is required. Please go back to the contact step.');
      return;
    }

    if (!email) {
      toast.error('Email is required. Please go back to the contact step.');
      return;
    }

    setIsProcessing(true);

    try {
      if (selectedCountry === 'nigeria') {
        // Use Paystack for Nigerian customers
        const reference = generateReference();
        
        const customer = {
          name: customerData.name,
          email: email,
          phone: phone
        };

        const payment = {
          amount: currentPlan.amount,
          reference,
          metadata: {
            custom_fields: [
              { name: 'plan_id', value: selectedPlan || '2-week' },
              { name: 'plan_title', value: currentPlan.title },
              { name: 'source', value: 'nocode_onboarding' }
            ]
          }
        };

        const authorizationUrl = await initiatePayment(customer, payment);
        
        // Redirect to Paystack payment page
        window.location.href = authorizationUrl;
      } else {
        // Use Stripe for international customers
        const result = await createPayment({
          customerName: customerData.name,
          customerEmail: email,
          customerPhone: phone || '+1',
          serviceType: 'No-Code Training Program',
          serviceTier: currentPlan.title,
          price: currentPlan.usdPrice,
          projectDescription: `No-Code Training - Plan: ${currentPlan.title}`
        });

        if (result.success) {
          // Stripe will handle redirection
          return;
        }
      }
    } catch (error: any) {
      console.error('Payment initiation failed:', error);
      toast.error('Payment initiation failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-md mx-auto bg-white min-h-screen overflow-y-auto"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex justify-between items-center">
        <div className="text-sm font-medium text-gray-600">Secure Payment</div>
        <div className="flex items-center gap-1 text-green-600">
          <Shield className="w-4 h-4" />
          <span className="text-xs">SSL Secured</span>
        </div>
      </div>

      <div className="px-4 pb-6 space-y-6">
        {/* Plan Summary */}
        <div className="text-center py-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Complete Your Purchase</h2>
          <p className="text-sm text-gray-600">Secure your spot in the No-Code Training Program</p>
        </div>

        {/* Selected Plan Card */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-blue-600">SELECTED PLAN</div>
            <CreditCard className="w-4 h-4 text-blue-600" />
          </div>
          <div className="font-semibold text-gray-800">{currentPlan.title}</div>
          <div className="text-2xl font-bold text-gray-800 mt-2">
            {selectedCountry === 'nigeria' ? currentPlan.total : currentPlan.usdTotal}
          </div>
        </div>

        {/* Country Selection */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800">Select your location:</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSelectedCountry('nigeria')}
              className={`p-3 rounded-xl border-2 transition-all ${
                selectedCountry === 'nigeria'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-1">🇳🇬</div>
                <div className="font-semibold text-gray-900">Nigeria/Africa</div>
                <div className="text-lg font-bold text-blue-600">{currentPlan.total}</div>
              </div>
            </button>
            <button
              onClick={() => setSelectedCountry('international')}
              className={`p-3 rounded-xl border-2 transition-all ${
                selectedCountry === 'international'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-1">🌍</div>
                <div className="font-semibold text-gray-900">International</div>
                <div className="text-lg font-bold text-blue-600">{currentPlan.usdTotal}</div>
              </div>
            </button>
          </div>
        </div>

        {/* Customer Information */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800">Billing Information</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <Input
              type="text"
              placeholder="Enter your full name"
              value={customerData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <Input
              type="email"
              value={email || 'No email collected'}
              disabled
              className="w-full bg-gray-50 text-gray-600"
              placeholder={!email ? 'Email not collected' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <Input
              type="tel"
              value={phone || 'No phone collected'}
              disabled
              className="w-full bg-gray-50 text-gray-600"
              placeholder={!phone ? 'Phone not collected' : ''}
            />
          </div>
        </div>

        {/* Payment Button */}
        <Button
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-xl text-lg"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </div>
          ) : (
            `Pay with ${selectedCountry === 'nigeria' ? `Paystack ${currentPlan.total}` : `Stripe ${currentPlan.usdTotal}`}`
          )}
        </Button>

        {/* Security Notice */}
        <div className="text-xs text-gray-500 text-center leading-relaxed">
          <div className="flex items-center justify-center gap-1 mb-2">
            <Shield className="w-3 h-3" />
            <span className="font-medium">Secure Payment</span>
          </div>
          Your payment information is encrypted and secure. Secured by {selectedCountry === 'nigeria' ? 'Paystack' : 'Stripe'} - 256-bit SSL encrypted.
        </div>

        {/* Payment Methods */}
        <div className="text-center">
          <div className="text-xs text-gray-400 mb-2">Accepted Payment Methods</div>
          <div className="flex justify-center gap-2 text-xs text-gray-400">
            <span>VISA</span>
            <span>•</span>
            <span>Mastercard</span>
            <span>•</span>
            <span>Verve</span>
            <span>•</span>
            <span>Bank Transfer</span>
          </div>
        </div>

        {/* Money-Back Guarantee */}
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">30-Day Money-Back Guarantee</span>
          </div>
          <p className="text-xs text-green-700 leading-relaxed">
            Not satisfied? Get a full refund within 30 days, no questions asked.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Step10Payment;